import { OUTCOMES } from './tradeSimEngine';
import { WIN_EXIT_COLOR, LOSS_EXIT_COLOR } from './replayAnnotations';

/**
 * THE SESSION'S TRADES, DRAWN ON THE CHART: one segment from Open to Close, per completed trade.
 *
 * <h2>What was missing</h2>
 *
 * The Interactive Trade Simulator's ledger reached the modeless card and nothing else. A reader
 * with thirty trades in a session could read the table and could not see where any of them sat on
 * the price action they were taken from — so "did I keep buying the same failed level" was a
 * question the feature could not answer, for want of a line.
 *
 * <h2>Why a VECTOR rather than two markers</h2>
 *
 * A pair of markers says WHEN a trade started and ended. The diagonal says what it DID: its slope
 * is the trajectory, its length is the holding period, and a glance across a session shows whether
 * the reader is catching moves or being chopped. Two markers a day apart with a flat line between
 * them look identical to two markers a day apart with a 4% climb.
 *
 * <p>This is the same argument {@code replayAnnotations.tradeVectors} makes for the ENGINE's book,
 * and the colours come from that module rather than being chosen again here — see below.
 *
 * <h2>ONE definition of won, imported, never re-derived</h2>
 *
 * A session trade carries {@code outcome}, which the engine and the manual machine both set, so
 * this asks it rather than comparing prices a second time. The failure a second derivation
 * produces is specific and hard to see: a vector drawn green ending at a marker drawn red, which
 * reads as a rendering bug rather than as two functions disagreeing about a sign.
 *
 * <h2>Scope: CTS, PTA, PTB and a bare MANUAL trade alike</h2>
 *
 * The brief names the three providers. Nothing here branches on one, because a manual trade taken
 * on a bare chart is as real a trade as one taken against a channel — it is in the same ledger and
 * in the same win rate — and a vector for every provider BUT that one would be a gap a reader
 * would read as a lost trade.
 */

/** The overlay id. One overlay carries the whole session, replaced wholesale on every change. */
export const TRADE_SIM_VECTOR_OVERLAY_ID = 'trade-sim-vectors';

/**
 * How thick a vector is drawn.
 *
 * <p>Thinner than the replay's 2.5: these sit on a chart that may also be carrying PTA and PTB
 * channels, their rays and their projections, where the replay's annotations have the canvas
 * largely to themselves.
 */
export const VECTOR_WIDTH = 2;

/**
 * How much of the colour reaches the canvas.
 *
 * <p>Not fully opaque, because a vector crosses the candles it was taken from and those candles
 * are the evidence for it. At 1.0 a dense session hides the price action the session is about.
 */
export const VECTOR_OPACITY = 0.85;

/** The colour of a vector, from the ONE place a won and a lost exit are coloured. */
export function vectorColor(trade) {
  return trade && trade.outcome === OUTCOMES.LOSS ? LOSS_EXIT_COLOR : WIN_EXIT_COLOR;
}

/**
 * Is this trade one we can draw a path for?
 *
 * <p>Four conditions, and each excludes a different thing:
 *
 * <ul>
 *   <li><b>Priced</b> — a click past the replay's assessment boundary is RECORDED and deliberately
 *       not evaluated. It has no exit because it was never walked.</li>
 *   <li><b>Not OPEN</b> — the engine's horizon has not run out yet. A vector to an exit that has
 *       not happened is either a line to nowhere or a line to an outcome nothing knows, and in a
 *       replay the second is the future arriving through the chart.</li>
 *   <li><b>Both indices are integers on the axis</b> — see {@link sessionTradeVectors}.</li>
 *   <li><b>Both prices are finite</b> — a NEUTRAL expiry has a real exit price; a refused trade
 *       has none.</li>
 * </ul>
 */
export function isDrawable(trade) {
  if (!trade || trade.priced === false) return false;
  if (trade.outcome === OUTCOMES.OPEN || !trade.outcome) return false;
  return Number.isInteger(trade.index)
    && Number.isInteger(trade.exitIndex)
    && Number.isFinite(trade.entryPrice)
    && Number.isFinite(trade.exitPrice);
}

/**
 * One drawable segment per completed trade, in the order they were taken.
 *
 * <h2>Indices to dates, through the axis the chart is actually drawing</h2>
 *
 * A trade stores BAR INDICES, which is what the click produced and what the engine walked. A
 * `markLine` coord is a CATEGORY VALUE, so the index has to be resolved against the same `dates`
 * array the series was built from. Doing it here rather than storing a date on the trade keeps the
 * ledger independent of which chart is on screen — the reader can change symbol, and a stale date
 * string would resolve to a bar on the new series rather than to nothing.
 *
 * <p>An index outside the axis yields NOTHING for that trade rather than a clamped end. A vector
 * with one end pinned to the edge misstates its slope and its duration at once, which are the two
 * things it exists to show.
 *
 * @param {Array} trades the session ledger, newest-last
 * @param {Array<string>} dates the chart's own category axis
 */
export function sessionTradeVectors(trades, dates) {
  const list = Array.isArray(trades) ? trades : [];
  const axis = Array.isArray(dates) ? dates : [];
  if (axis.length === 0) return [];

  return list
    .filter(isDrawable)
    .map((t) => {
      const date1 = axis[t.index];
      const date2 = axis[t.exitIndex];
      if (typeof date1 !== 'string' || typeof date2 !== 'string') return null;
      return {
        date1,
        price1: t.entryPrice,
        date2,
        price2: t.exitPrice,
        color: vectorColor(t),
        provider: t.provider || 'MANUAL',
        direction: t.direction || null,
        outcome: t.outcome,
        // What a hover would say, if these ever stop being silent. Carried now so the decision is
        // a one-line change rather than a reconstruction of which trade a segment came from.
        title: `${t.provider || 'MANUAL'} ${t.direction === 'down' ? 'SHORT' : 'LONG'}`
          + ` ${date1} → ${date2}`
          + (Number.isFinite(t.pnlPercent) ? ` (${t.pnlPercent >= 0 ? '+' : ''}${t.pnlPercent.toFixed(2)}%)` : ''),
      };
    })
    .filter(Boolean);
}

/**
 * The overlay for a whole session, or NULL when there is nothing to draw.
 *
 * <p>Null rather than an overlay with an empty list, so the caller removes the series instead of
 * leaving an empty one on the chart. An empty series is not free — it is merged, laid out and
 * z-sorted on every option rebuild — and more importantly it makes "the session has no completed
 * trades" and "the overlay failed to build" the same thing on screen.
 */
export function buildTradeSimVectorOverlay(trades, dates) {
  const lines = sessionTradeVectors(trades, dates);
  if (lines.length === 0) return null;
  return {
    id: TRADE_SIM_VECTOR_OVERLAY_ID,
    type: 'tradesimvectors',
    label: 'Session trades',
    lines,
  };
}
